// Визуальные словари клиента: цвет стихии, цвет печати Цолькина, короткие
// имена дворцов. Вынесено из экранов, потому что одни и те же цвета нужны и
// плиткам «Циклов», и графике (ElementFlow, PalaceRing, LifeScale): разошедшиеся
// копии палитры — первый шаг к тому, что месяц на плитке зелёный, а на схеме
// охряный.

import type { PillarDto, SealDto } from '../contracts/cycles'

export type ElementId = PillarDto['element']

/** Пять стихий в порядке порождения: дерево → огонь → земля → металл → вода. */
export const ELEMENT_ORDER: readonly ElementId[] = ['wood', 'fire', 'earth', 'metal', 'water']

export const ELEMENT_COLORS: Record<ElementId, string> = {
  wood: 'var(--green)',
  fire: 'var(--fire)',
  earth: 'var(--ochre)',
  metal: 'var(--silver)',
  water: 'var(--night)',
}

export const ELEMENT_RU: Record<ElementId, string> = {
  wood: 'Дерево',
  fire: 'Огонь',
  earth: 'Земля',
  metal: 'Металл',
  water: 'Вода',
}

/** Цвет стихии по строке из ответа сервера; неизвестная стихия — нейтральный. */
export function elementColor(element: string | null | undefined): string {
  if (!element) return 'var(--text3)'
  return ELEMENT_COLORS[element as ElementId] ?? 'var(--text3)'
}

export function elementRu(element: string | null | undefined): string {
  if (!element) return ''
  return ELEMENT_RU[element as ElementId] ?? element
}

/**
 * /api/systems отдаёт «в избытке» и «совсем нет» русскими названиями, а
 * распределение — идентификаторами. Приводим к идентификатору обе формы,
 * иначе подсветка на схеме молча не срабатывает.
 */
const ELEMENT_BY_RU = new Map<string, ElementId>(
  (Object.keys(ELEMENT_RU) as ElementId[]).map((id) => [ELEMENT_RU[id].toLowerCase(), id]),
)

export function elementIdFrom(value: string): ElementId | null {
  if (value in ELEMENT_COLORS) return value as ElementId
  return ELEMENT_BY_RU.get(value.trim().toLowerCase()) ?? null
}

/** Четыре цвета печатей Цолькина. Синий берём фиолетовым: ночной на тёмном фоне не читается. */
export const SEAL_COLORS: Record<SealDto['color'], string> = {
  red: 'var(--bordo)',
  white: 'var(--milk)',
  blue: 'var(--violet)',
  yellow: 'var(--gold)',
}

export function sealColor(seal: SealDto): string {
  return SEAL_COLORS[seal.color] ?? 'var(--gold)'
}

/**
 * Короткие подписи дворцов: в кольце и на шкале «Перемены и поездки» не
 * помещаются ни в сегмент, ни в подпись под ним.
 */
export const PALACE_SHORT: Record<string, string> = {
  life: 'Судьба',
  siblings: 'Братья',
  spouse: 'Пара',
  children: 'Дети',
  wealth: 'Деньги',
  health: 'Здоровье',
  travel: 'Дороги',
  friends: 'Друзья',
  career: 'Дело',
  property: 'Дом',
  fortune: 'Дух',
  parents: 'Родители',
}

export function palaceShort(id: string, fallback = ''): string {
  return PALACE_SHORT[id] ?? fallback
}

/**
 * Цвет символа приходит от сервера шестнадцатеричным (`#a34d66`): полупрозрачную
 * заливку под глиф считаем из него сами. Не hex — отдаём как есть, лишь бы не
 * рисовать пустоту; color-mix тут нельзя, старый вебвью его не знает.
 */
export function withAlpha(color: string, alpha: number): string {
  const match = /^#([0-9a-f]{6})$/i.exec(color.trim())
  if (!match) return color
  const value = Number.parseInt(match[1] as string, 16)
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`
}
